// De pagina die een klant te zien krijgt achter een gedeelde link.
//
// Joris, 11 sep 2026: doorsturen mag, kijken mag, downloaden per link
// instelbaar. Dus geen inlog, geen menu's, geen andere video's — alleen deze
// selectie. De link verloopt vanzelf; daarna staat hier dat hij verlopen is.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { AI_ENGINE_URL } from '../../utils/apiConfig';

export default function PortfolioPagina() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    fetch(`${AI_ENGINE_URL}/api/v1/portfolio/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (r.status === 410) throw new Error('verlopen');
        if (!r.ok) throw new Error('onbekend');
        return r.json();
      })
      .then(setData)
      .catch((e) => setFout(e.message));
  }, [token]);

  if (fout) {
    return (
      <Kader>
        <Typography sx={{ fontSize: 20, fontWeight: 800, mb: 1 }}>
          {fout === 'verlopen' ? 'Deze selectie is verlopen' : 'Deze selectie bestaat niet'}
        </Typography>
        <Typography sx={{ color: '#64748b' }}>
          {fout === 'verlopen'
            ? 'Vraag je contactpersoon bij Full Frame om een nieuwe link.'
            : 'Controleer of je de hele link hebt gekopieerd.'}
        </Typography>
      </Kader>
    );
  }

  if (!data) {
    return <Kader><CircularProgress size={26} /></Kader>;
  }

  const verloopt = data.verlooptOp ? new Date(data.verlooptOp) : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 4, md: 7 } }}>
      <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#94a3b8', mb: 1 }}>
          Full Frame
        </Typography>
        <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
          {data.titel}
        </Typography>
        {data.intro && (
          <Typography sx={{ color: '#475569', fontSize: 16, mb: 3, maxWidth: 640 }}>
            {data.intro}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 3 }}>
          {data.videos.map((v) => (
            <Box key={v.id} sx={{ bgcolor: '#fff', borderRadius: 3, overflow: 'hidden',
              border: '1px solid #e2e8f0' }}>
              <video
                src={v.videoUrl}
                poster={v.thumbnailUrl || undefined}
                controls
                controlsList={data.magDownloaden ? undefined : 'nodownload'}
                style={{ width: '100%', display: 'block', backgroundColor: '#000' }}
              />
              <Box sx={{ p: 2.5 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{v.titel}</Typography>
                {v.omschrijving && (
                  <Typography sx={{ color: '#64748b', fontSize: 14, mt: 0.5 }}>
                    {v.omschrijving}
                  </Typography>
                )}
                {data.magDownloaden && (
                  <Button size="small" variant="outlined" sx={{ mt: 1.5, textTransform: 'none' }}
                    onClick={() => window.open(v.videoUrl, '_blank', 'noopener,noreferrer')}>
                    Downloaden
                  </Button>
                )}
              </Box>
            </Box>
          ))}
          {data.videos.length === 0 && (
            <Typography sx={{ color: '#64748b' }}>Deze selectie is leeg.</Typography>
          )}
        </Box>

        <Typography sx={{ color: '#94a3b8', fontSize: 12.5, mt: 5 }}>
          {verloopt
            ? `Deze link werkt tot ${verloopt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}.`
            : ''}
          {' '}Vragen? Neem contact op met je contactpersoon bij Full Frame.
        </Typography>
      </Box>
    </Box>
  );
}

function Kader({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center',
      justifyContent: 'center', textAlign: 'center', p: 3 }}>
      <Box sx={{ maxWidth: 420 }}>{children}</Box>
    </Box>
  );
}
